//@flow
import ko from 'rgs-knockout';

import _ from 'lodash';
import kv from 'rgs-knockout-validation';
import vb from 'validation-builder';
import { ValidatableMixin } from 'validatable';

import extendedKladrUtils from 'extended-kladr-utils';
import { compactJoin } from 'common-utils';

import {
	ExtendedAddress,
	ExtendedAddressHouse,
	ExtendedAddressStreet,
	ExtendedAddressSettlement,
} from 'calc-common';

import type {
	AddressOptions,
	AddressInterface,
	AddressHouseInterface,
	AddressStreetInterface,
	AddressSettlementInterface,
} from 'calc-common';

import type { Computed } from 'rgs-knockout';

class AddressHouse
	extends ValidatableMixin(ExtendedAddressHouse)
	implements AddressHouseInterface {
	constructor (options: *, registration: Address, isRequired: Computed<boolean>) {

		const dictionary = options.dictionaries.kladr.housesHashMap;
		const zipCodeDictionary = options.dictionaries.kladr.zipCodeHashMap;

		super({ address: registration, dictionary });

		this._registration = registration;

		this.Name = ko.map(ko.observable(), {
			mapRead: v => v,
			mapWrite: (name: ?string) => {
				let kladr;

				if (this._registration.Settlement.hasNoStreetsOrHouses()) {
					kladr = this._registration.Settlement.Kladr();
				} else if (this._registration.Street.hasNoHouses()) {
					kladr = this._registration.Street.Kladr();
				} else {
					kladr = dictionary.get(this._getDictionaryKey(name));
				}

				if (kladr && name) {
					this.Kladr(extendedKladrUtils.normalizeHouseKladr(kladr));
					return name;
				}

				this.Kladr(null);
				return name;
			},
		})
			.extend({ toFromJS: true })
			.extend({
				validation: vb.all([
					vb.addressHouse(),
					vb.if(isRequired, vb.required()),
				])
			});

		this.ZipCode = ko.pureComputed(() => {
			const key = {
				Kladr: this.Kladr(),
				Name: this.Name(),
			};

			return zipCodeDictionary.get(key);
		});
	}

	validationArray () {
		return _.filter(this, kv.utils.isValidatable);
	}
}

class AddressStreet
	extends ValidatableMixin(ExtendedAddressStreet)
	implements AddressStreetInterface {
	constructor (options: *, registration: Address, isRequired: Computed<boolean>) {

		const dictionary = options.dictionaries.kladr.streets;
		const zipCodeDictionary = options.dictionaries.kladr.zipCode;

		super({ address: registration, dictionary });

		const streetRequired = ko.pureComputed(() => registration.HasStreets() && isRequired());

		this.Kladr.extend({ validation: vb.if(streetRequired, vb.required()) });

		this.Name.extend({ validation: vb.if(streetRequired, vb.required()) });

		this.ZipCode = ko.pureComputed(() => zipCodeDictionary.get(this.Kladr()));
	}

	validationArray () {
		return _.filter(this, kv.utils.isValidatable);
	}
}

class AddressSettlement
	extends ValidatableMixin(ExtendedAddressSettlement)
	implements AddressSettlementInterface {
	constructor (options: *, registration: Address, isRequired: Computed<boolean>) {

		const dictionary = options.dictionaries.kladr.settlements;
		const zipCodeDictionary = options.dictionaries.kladr.zipCode;

		super({ address: registration, dictionary });

		this._options = options;

		this.Kladr.extend({ validation: vb.if(isRequired, vb.required()) });

		this.Name.extend({ validation: vb.if(isRequired, vb.required()) });

		this.ZipCode = ko.pureComputed(() => zipCodeDictionary.get(this.Kladr()));

		this.settlementWithAreaRepr = ko.map(ko.observable(), {
			mapRead: () => {
				if (this.Name()) {
					const isNeedRegionName = !extendedKladrUtils.isFederalSettlement(
						this.Kladr(),
					);

					return compactJoin(...[
						this.Name(),
						this.CityName(),
						this.AreaName(),
						isNeedRegionName ? this.getRegionName() : null,
					]);
				}
				return '';
			},
			mapWrite: v => {
				if (!v) {
					this.AreaName(null);
					this.CityName(null);
					this.Name(null);
				}
				return v;
			},
		});
	}

	getRegionName = (): string => {
		let regionName = '';

		const kladr = this.Kladr();
		if (!kladr) {
			return regionName;
		}

		const regionId = _.toNumber(extendedKladrUtils.getRegionCode(kladr));
		const region = this._options.dictionaries.kladr.regions.get(regionId);
		regionName = region.Name || '';
		return regionName;
	};

	validationArray () {
		return _.filter(this, kv.utils.isValidatable);
	}
}

export default class Address
	extends ValidatableMixin(ExtendedAddress)
	implements AddressInterface {
	reliableZipCode: Computed<?string>;
	userDefinedZipCode: Computed<?string>;
	_locationFilled: Computed<boolean>;

	constructor (options: *, isRequired: Computed<boolean>) {

		const { kladr } = options.dictionaries;

		const addressOptions: AddressOptions = {
			dictionaries: {
				kladr: {
					streets: kladr.streets,
					settlements: kladr.settlements,
					houses: kladr.housesHashMap,
				},
			},
		};

		super(addressOptions);

		this.HasStreets(true);

		this.Settlement = new AddressSettlement(options, this, isRequired);

		this.Street = new AddressStreet(options, this, isRequired);

		this.House = new AddressHouse(options, this, isRequired);

		this.Apartement.extend({ validation: vb.addressApartment('Заполнено неверно') });

		this._locationFilled = ko.pureComputed(() => this.House.Name()
			&& (this.Street.Kladr() || !this.HasStreets())
			&& this.Settlement.Kladr(),
		);

		this.reliableZipCode = ko.pureComputed(() => this._locationFilled()
			? this.House.ZipCode()
				|| (this.HasStreets() && this.Street.ZipCode())
				|| this.Settlement.ZipCode()
			: null,
		);

		this.userDefinedZipCode = ko.observable()
			.extend({ notify: 'always' });

		this.ZipCode = ko.map(this.userDefinedZipCode, {
			mapRead: v => v || this.reliableZipCode(),
			mapWrite: v => v,
		})
			.extend({
				validation: vb.all([
					vb.if(isRequired, vb.required()),
					vb.pattern(/^[0-9]{6}$/),
				]),
			})
			.extend({ notify: 'always' })
			.extend({ toFromJS: true });
	}

	resetUserDefinedZipCodeOnKladrZipCodeExistsHandler (address: KaskoAddress): void {
		if (address.reliableZipCode()) {
			address.userDefinedZipCode(null);
			address.ZipCode.isModified(true);
		}
	}

	validationArray (): Array<*> {
		let arr = [
			...this.Settlement.validationArray(),
			...this.Street.validationArray(),
			...this.House.validationArray(),
			this.ZipCode,
		];

		return _.uniq(arr);
	}
}
